import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import {
  numberWithComma,
  formatDateWithoutTime,
  getDiscountType,
  mappingConfigSign,
  getDefaultValueConfig,
} from 'helpers/format'
import { Col, Row, Space } from 'antd'
import { DiscountType, menuTaxSignType, Taxtatus, VAT_TYPE, VAT_TYPE_TEXT } from 'helpers/constanst'
import { TaxData } from 'Store/tax-management/types'
import PdfCompanyInfo from './PdfCompanyInfo'
import { ThaiBaht } from 'thai-baht-text-ts'
import { SupplierOrderData } from 'Store/supplier-order/types'
import { QuotationProduct } from 'Store/quotation/types'
import { PdfPrintFormat } from 'Components/form'
import { ReturnTag } from 'Components/utilities'
import { ConfigData } from 'Store/pos/types'

type Props = {
  detail?: TaxData
  orderDetail?: SupplierOrderData
  configData?: ConfigData
  title: string
}

const SupplierTaxPDF: React.FunctionComponent<Props> = props => {
  const { detail, orderDetail, configData, title } = props
  const includeVat = detail?.vat_amount === 7 && orderDetail?.vat_type !== VAT_TYPE.EX
  const config = configData && configData?.config_value && JSON.parse(configData?.config_value || '')

  const products = orderDetail?.products || []

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const getTotalProductPrice = () => {
    let total = 0
    products.forEach((item: QuotationProduct) => {
      total += item.price * item.quantity
    })
    return total
  }

  const renderInfoItem = (title: any, value: any) => {
    return (
      <Row justify="space-between">
        <Col>
          <InfoTitle>{title}</InfoTitle>
        </Col>
        <Col offset={1}>
          <InfoValue>{value || '-'}</InfoValue>
        </Col>
      </Row>
    )
  }

  const info = (title: any, value: any, justify: 'start' | 'end' = 'start') => {
    return (
      <Row gutter={[8, 8]} justify={justify}>
        <Col>
          <Space wrap={true}>
            <InfoTitle>
              {title} : {value || '-'}
            </InfoTitle>
          </Space>
        </Col>
      </Row>
    )
  }

  const renderCustomerInfo = () => {
    return (
      <Row justify="space-between">
        <Col span={12}>
          <Row>
            <Col span={24}> {info('ลูกค้า', `${detail?.name}`)}</Col>
            <Col span={24}> {info('ที่อยู่', detail?.address)}</Col>
            <Col span={24}>{info('เบอร์โทรศัพท์', detail?.mobile)}</Col>
            <Col span={24}> {info('เลขประจำตัวผู้เสียภาษี', detail?.taxpayer_no)}</Col>
            {detail?.note !== '' && <Col span={24}> {info('หมายเหตุ', detail?.note)}</Col>}
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Col span={24}>{info('วันที่', `${formatDateWithoutTime(detail?.created_at || '')}`, 'end')}</Col>
            <Col span={24}>{info('เลขที่', `${detail?.tax_invoice_no || ''}`, 'end')}</Col>
            <Col span={24}>{info('อ้างอิง', `${orderDetail?.sale_order_no || ''}`, 'end')}</Col>
          </Row>
        </Col>
      </Row>
    )
  }

  const renderProductName = (item: string, record: QuotationProduct) => {
    const isSameTitle = record.title == record.product_option_title
    return (
      <>
        <RowTile>{record.title}</RowTile>
        {!isSameTitle && <ProductDesc>{record.product_option_title}</ProductDesc>}
        <ReturnTag isReturn={record?.is_return} isText={true} returnUom={record?.product_uom_system || ''} />
      </>
    )
  }

  const getDiscount = () => {
    const totalPrice = getTotalProductPrice()
    let discount: number = orderDetail?.discount || 0
    if (orderDetail?.discount_type === 'percent') {
      discount = (totalPrice * discount) / 100
    }
    return discount
  }

  const totalProductPrice = getTotalProductPrice()
  const discountThb = getDiscount()
  const percentVat = detail?.vat_amount === undefined ? 7 : detail?.vat_amount
  const refund = detail?.refund_amount || 0
  const hasProductDiscount = (orderDetail?.total_product_discount || 0) > 0
  const productPriceAfterProductDiscount =
    (orderDetail?.total_product_price_before_discount || 0) - (orderDetail?.total_product_discount || 0)
  const tailDiscount = (orderDetail?.discount || 0) > 0
  const isExcludeVat = orderDetail?.vat_type === VAT_TYPE.EX
  const priceBeforVat = productPriceAfterProductDiscount - discountThb - refund

  const summaryFooter = (
    <Row justify="space-between" gutter={[8, 8]} align="bottom">
      <Col span={12}>
        <InfoTitle>จำนวนเงินรวมทั้งสิ้น (ตัวอักษร) {ThaiBaht(detail?.net_price || 0)}</InfoTitle>
      </Col>
      <Col span={12}>
        {hasProductDiscount &&
          renderInfoItem(
            'รวมราคาสินค้าก่อนหักส่วนลด',
            `${numberWithComma(orderDetail?.total_product_price_before_discount, true)} บาท`,
          )}
        {hasProductDiscount &&
          renderInfoItem(
            'หัก ส่วนลดสินค้า',
            `${numberWithComma(orderDetail?.total_product_discount, true)} บาท`,
          )}
        {renderInfoItem('รวมราคาสินค้า', `${numberWithComma(productPriceAfterProductDiscount, true)} บาท`)}
        {tailDiscount &&
          renderInfoItem(
            `หัก ส่วนลดท้ายบิล ${
              orderDetail?.discount_type === DiscountType.PERCENT
                ? ` (${numberWithComma(orderDetail?.discount || 0)}${getDiscountType(
                    orderDetail?.discount_type || '',
                  )})`
                : ''
            }`,
            `${numberWithComma(discountThb, true)} บาท`,
          )}
        {refund > 0 && renderInfoItem('หัก คืนเงิน/คืนสืนค้า', `${numberWithComma(refund, true)} บาท`)}
        {(tailDiscount || refund > 0) &&
          renderInfoItem('ราคารวมทั้งสิ้น ', `${numberWithComma(priceBeforVat, true)} บาท`)}
        {/* {renderInfoItem(
          `ภาษีมูลค่าเพิ่ม ${percentVat}% (${
            orderDetail?.vat_type === VAT_TYPE.EX ? VAT_TYPE_TEXT.EX : VAT_TYPE_TEXT.IN
          })`,
          `${numberWithComma(orderDetail?.vat_amount || 0, true)} บาท`,
        )} */}
        {!isExcludeVat &&
          renderInfoItem(
            'ราคาก่อนรวมภาษีมูลค่าเพิ่ม ',
            `${numberWithComma(priceBeforVat - (detail?.vat_amount_cal || 0), true)} บาท`,
          )}
        {renderInfoItem(
          `ภาษีมูลค่าเพิ่ม ${percentVat}% (${
            orderDetail?.vat_type === VAT_TYPE.EX ? VAT_TYPE_TEXT.EX : VAT_TYPE_TEXT.IN
          })`,
          `${numberWithComma(detail?.vat_amount_cal, true)} บาท`,
        )}
        {renderInfoItem('จำนวนเงินรวมทั้งสิ้น', `${numberWithComma(detail?.net_price || 0, true)} บาท`)}
      </Col>
    </Row>
  )

  const tableHeader = (
    <thead>
      <th>ลำดับ</th>
      {/* <th>รหัสสินค้า</th> */}
      <th className="left" style={{ width: '30%' }}>
        รายการ
      </th>
      {/* <th>ยี่ห้อ</th> */}
      <th>จำนวน</th>
      <th>หน่วย</th>
      <th className="right">ราคา/หน่วย</th>
      <th className="right">ส่วนลด/หน่วย</th>
      <th className="right">{includeVat ? 'ราคารวมภาษี' : 'ราคารวม'}</th>
    </thead>
  )

  const filterProducts = (products: QuotationProduct[]) => {
    //return products && products?.filter(item => item.is_return !== true)
    return products
  }

  const productResult = filterProducts(products)

  const tableBody = (
    <tbody>
      {productResult &&
        productResult.map((item: QuotationProduct, index: number) => {
          return (
            <tr key={index}>
              <td>{renderInfo(index + 1)}</td>
              {/* <td>{renderInfo(item?.product?.code || '-')}</td> */}
              <td className="left">{renderProductName(item?.title, item)}</td>
              {/* <td>{renderInfo(item?.product?.manufacturer?.name || '-')}</td> */}
              <td>{renderInfo(numberWithComma(item?.quantity))}</td>
              <td>{item?.unit}</td>
              <td className="right">{renderInfo(numberWithComma(item.price, true))}</td>
              <td className="right">{renderInfo(numberWithComma(item.discount, true))}</td>
              <td className="right">{renderInfo(numberWithComma(item.price * item.quantity, true))}</td>
            </tr>
          )
        })}
    </tbody>
  )

  const remarkFooter = () => {
    const remark = config?.remark || ''
    return remark ? (
      <Row justify="start" gutter={[8, 8]} align="bottom">
        <Col span={24}>
          <Remark>{remark}</Remark>
        </Col>
      </Row>
    ) : null
  }

  return (
    <>
      <PdfPrintFormat
        companyInfo={<PdfCompanyInfo merchant={detail?.tax_invoice_config} />}
        title={title}
        leftInfo={renderCustomerInfo()}
        leftInfoSpan={24}
        // rightInfo={taxInfo}
        footerSpan={7}
        summaryFooter={summaryFooter}
        signPosition={config?.signature ? config?.signature || [] : getDefaultValueConfig(menuTaxSignType)}
        thead={tableHeader}
        tbody={tableBody}
        moreFooter={
          detail?.status === Taxtatus.CANCEL ? (
            <Row justify="space-between" gutter={[8, 8]} align="bottom">
              <Col span={12}>{info('หมายเหตุ', `${detail?.reason}`)}</Col>
            </Row>
          ) : null
        }
        remarkFooter={remarkFooter()}
      />
      {detail?.status === Taxtatus.CANCEL && (
        <Cancel>
          <CancelText>ยกเลิก</CancelText>
        </Cancel>
      )}
    </>
  )
}

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  text-align: left;
`

const InfoValue = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-right: 10px;
`

const Cancel = styled.div`
  position: absolute;
  top: 250px;
  left: 0;
  right: 0;
`

const CancelText = styled.div`
  text-align: center;
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  font-size: 100px;
  /* Safari */
  -webkit-transform: rotate(-45deg);

  /* Firefox */
  -moz-transform: rotate(-45deg);

  /* IE */
  -ms-transform: rotate(-45deg);

  /* Opera */
  -o-transform: rotate(-45deg);

  width: 0;
  height: 0;
  overflow: hidden;

  @media print {
    width: 100%;
    height: auto;
  }
`

const Remark = styled.div`
  text-align: left;
`

export default SupplierTaxPDF
